import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { G2Info } from '../types';
import { G2ConnectModal } from './G2ConnectModal';
import G2SettingModal from './G2SettingModal';
import { useG2 } from './useG2';

export const G2Item = (props: IntegrationItemProps) => {
  const { isConnector } = props;
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const { g2Workspace, disconnectG2Mutation, requestImportMutation, refetch } =
    useG2(!isConnector);

  const connectModalState = useModalState();
  const settingModalState = useModalState();
  const importModalState = useModalState<{
    workspace: G2Info;
    isNew: boolean;
  }>();

  const handleConnectComplete = async (workspace: G2Info) => {
    if (!currWorkspace) return;
    await connectModalState.close();
    await fetchFeedbackSources(currWorkspace.alias);
    await refetch();
    // G2 특수성: 연결 성공 후 Setting 모달 자동 오픈
    settingModalState.open();
  };

  const handleDisconnect = () => {
    disconnectG2Mutation();
  };

  const handleImportWorkspace = async (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!g2Workspace}
        source={SOURCE_TYPE.G2}
        description="Import reviews as feedbacks into Syncly."
        connectedDisplay="G2"
        info={g2Workspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => connectModalState.open()}
        additionalActions={(workspace: G2Info) => (
          <div>
            <Button variant="outlined" onClick={() => settingModalState.open()}>
              Setting
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace: workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
          </div>
        )}
      />
      <Modal
        status={connectModalState.status}
        closeModal={connectModalState.close}
      >
        <G2ConnectModal onConnectComplete={handleConnectComplete} />
      </Modal>
      <Modal
        status={settingModalState.status}
        closeModal={settingModalState.close}
        useBackgroundClose
      >
        <G2SettingModal workspace={g2Workspace || null} refetch={refetch} />
      </Modal>
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
      >
        <IntegrationImportModal
          isNew={importModalState.modalState?.isNew || false}
          title={'Import Tickets'}
          subTitle={'G2'}
          subTitleList={g2Workspace?.alias}
          historyList={g2Workspace?.history || []}
          onSubmitImport={(start_date, end_date) =>
            handleImportWorkspace({ start_date, end_date })
          }
        />
      </Modal>
    </>
  );
};
